import * as React from 'react'
// components
import InputLabel from 'components/FormElements/components/InputLabel'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
// icons
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
// views
import { IconButton, Input } from './views'
// types
import { InputProps } from '@mui/material/Input'
// utils
import { debounce } from 'lodash'

export interface CustomInputProps extends InputProps {
  value?: string
  label?: string
  shrink?: boolean
  isError?: boolean
  required?: boolean
  debounced?: boolean
  isFocused?: boolean
  inputColor?: string
  labelStyles?: any
  withEllipsis?: boolean
  handleOnBlur?: (event?: React.MouseEvent) => void
  handleOnFocus?: () => void
  disableUnderline?: boolean
}

const InputComponent: React.FC<CustomInputProps> = ({
  type,
  value: internalValue,
  label,
  shrink,
  isError,
  onChange,
  required,
  debounced,
  fullWidth = false,
  labelStyles = {},
  endAdornment,
  handleOnBlur,
  handleOnFocus,
  ...rest
}) => {
  const [value, setValue] = React.useState('')
  const [isFocused, setFocus] = React.useState(false)
  const [showPassword, setShowPassword] = React.useState(false)

  const onFocus = () => {
    !isFocused && setFocus(true)
    handleOnFocus?.()
  }

  const onBlur = () => {
    isFocused && setFocus(false)
    handleOnBlur?.()
  }

  const debouncedOnChange = React.useCallback(
    debounce((value: React.ChangeEvent<HTMLInputElement>) => {
      onChange(value)
    }, 300),
    [onChange]
  )

  const handleOnChange = (value: React.ChangeEvent<HTMLInputElement>) => {
    setValue(value.target.value)

    if (debounced) {
      debouncedOnChange(value)
    } else {
      onChange(value)
    }
  }

  const handleClickShowPassword = () => setShowPassword((prev) => !prev)

  const isPassword = type === 'password'
  // 0 is a value: only an empty string, null or undefined leave the label down
  const hasValue = value !== '' && value !== null && value !== undefined
  const isLabelShrink = shrink || isFocused || hasValue

  React.useEffect(() => {
    if (internalValue !== value) {
      setValue(internalValue)
    }
  }, [internalValue])

  return (
    <FormControl error={isError} aria-describedby='name-error-text' fullWidth={fullWidth}>
      {label && (
        <InputLabel htmlFor={`${rest.name}-input`} style={labelStyles} shrink={isLabelShrink} required={required}>
          {label}
        </InputLabel>
      )}
      <Input
        {...rest}
        onBlur={onBlur}
        onFocus={onFocus}
        autoComplete={'off'}
        fullWidth={fullWidth}
        id={`${rest.name}-input`}
        onChange={handleOnChange}
        showAdornment={isLabelShrink}
        type={showPassword ? 'text' : type}
        value={value}
        endAdornment={
          isPassword ? (
            <InputAdornment position='end'>
              <IconButton onClick={handleClickShowPassword}>
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ) : (
            endAdornment
          )
        }
      />
    </FormControl>
  )
}

export default InputComponent
